import React from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import moment from 'moment-mini';

import {
  Card,
  CardActionArea,
  CircularProgress,
  Typography,
  Tooltip,
  Chip,
  Box,
} from '@mui/material';
import {
  fetchLinkedSources,
  fetchLinkedSource,
  fetchLinkedFlows,
  fetchSupportedFlows,
  fetchJob,
} from '../../redux/actions';
import { getSelectedFlow, isJobPending } from '../../redux/selectors';
import * as UIAction from '../../redux/actions/ui.actions';
import * as Utils from './SourceCardUtil';
import { getLogoUrl } from '@hotglue/common';
import { WarningAmberOutlined, WorkOutline } from '@mui/icons-material';


const SourceCard = ({ source, flowId, tenantId }) => {
  const lastJob = useSelector((state) => state.jobs.lastJob[`${tenantId}/${flowId}/${source.tap}`]);
  const pendingJob = useSelector((state) => isJobPending(state, lastJob?.s3_root, flowId, tenantId));
  const hideJobStatusBadge = useSelector((state) => state.configuration.hideJobStatusBadge);
  const dispatch = useDispatch()
  React.useEffect(() => {
    if (!lastJob || lastJob.flow_id !== flowId) {
      // If there is no last job, check if there is one
      dispatch(fetchJob(undefined, undefined, source.tap, 'source'));
    }
  }, [lastJob]);

  React.useEffect(() => {
    if (
      !pendingJob &&
      lastJob &&
      lastJob.status &&
      !(lastJob.status.includes('FAILED') || lastJob.status.includes('COMPLETE'))
    ) {
      console.log(`Polling job ${lastJob.job_id}`);
      Utils.pollJob(lastJob.s3_root, flowId, tenantId, source.tap);
    }
  }, [lastJob]);

  const { label, version, tap } = source;
  const icon = getLogoUrl(source);
  const isFile = tap === 'file';

  // Actions

  const onClickBody = () => {
    UIAction.goToEntityConfig(source, 'source');
  };

  return (
    <Card
      elevation={0}
      sx={{
        height: '160px',
        cursor: isFile ? 'pointer' : '',
        border: ({ palette }) => `1px solid ${palette.common.outline}`,
      }}
    >
      <CardActionArea
        id="LinkedSourceCard"
        onClick={onClickBody}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          height: '100%',
          position: 'relative',
        }}
      >
        <Box top={0} right={0} left={0} position="absolute" height={'32px'}>
          <Box
            height={'32px'}
            width="100%"
            sx={{ position: 'relative', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}
          >
            {pendingJob && !hideJobStatusBadge ? (
              <Tooltip title={`Job Running`} placement="top">
                <Box
                  position="relative"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="28px"
                  width="28px"
                  role='tooltip'
                  aria-label='running job tooltip'
                  mt={1}
                  mr={1}
                >
                  <WorkOutline sx={{ fontSize: '13px', position: 'absolute' }} />

                  <CircularProgress
                    size={28}
                    sx={{
                      color: '#FA5240',
                      position: 'absolute',
                      zIndex: 1,
                    }}
                  />
                </Box>
              </Tooltip>
            ) : lastJob && !hideJobStatusBadge ? (
              lastJob.status === 'JOB_COMPLETED' ? (
                <Tooltip
                  title={`Last synced at ${moment(lastJob.start_time).format('HH:mm MM/DD/YY')}`}
                  placement="top"
                >
                  <Box
                    border="1px solid #36C53C"
                    width={28}
                    height={28}
                    borderRadius="32px"
                    display="flex"
                    alignItems="center"
                    role='tooltip'
                    aria-label={`Last synced at ${moment(lastJob.start_time).format('HH:mm MM/DD/YY')}`}
                    justifyContent="center"
                    mt={1}
                    mr={1}
                  >
                    <WorkOutline sx={{ fontSize: '13px' }} />
                  </Box>
                </Tooltip>
              ) : (
                <Tooltip
                  title={`Sync failed at ${moment(lastJob.start_time).format('HH:mm MM/DD/YY')}`}
                  placement="top"
                >
                  <Box
                    border="1px solid #FA2A52"
                    width={28}
                    role='tooltip'
                    aria-label={`Sync failed at ${moment(lastJob.start_time).format('HH:mm MM/DD/YY')}`}
                    height={28}
                    borderRadius="32px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mt={1}
                    mr={1}
                  >
                    <WarningAmberOutlined sx={{ fontSize: '13px', color: '#FA2A52' }} />
                  </Box>
                </Tooltip>
              )
            ) : (
              <Box display="flex" role='tooltip' aria-label='linked tooltip'bgcolor="success.light" borderRadius="10px" mr={1}>
                <Typography variant="body2" fontSize="8px" color="success.dark" sx={{ px: 1, py: '2px' }}>
                  LINKED
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box display="flex" flexDirection="column" gap={2} mt={1} alignItems="center">
          <Box component="img" role={'img'} alt={`image for ${label} source`} width="48px" height="48px" src={icon} sx={{ objectFit: 'contain' }} />
          <Typography variant="h2" textAlign="center">
            {label}
          </Typography>
        </Box>
        {version && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 10,
            right: 10,
          }}
        >
            <Chip
              role='tooltip'
              aria-label='source version tooltip'
              label={version}
              size="small"
              sx={{ color: '#676767', fontSize: '10px', height: '18px', fontWeight: '300' }}
            />
        </Box>
        )}
      </CardActionArea>
    </Card>
  );
};

const mapStateToProps = (state, props) => {
  const flowId = getSelectedFlow();

  return {
    flowId,
    tenantId: state.configuration.tenant_id,
    demo: state.configuration.demo || state.preferences.demoMode,
    multipleTenantLinks: state.preferences.multipleTenantLinks,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchLinkedSource: (tap) => dispatch(fetchLinkedSource(tap)),
    fetchLinkedSources: () => dispatch(fetchLinkedSources()),
    fetchLinkedFlows: () => dispatch(fetchLinkedFlows()),
    fetchSupportedFlows: () => dispatch(fetchSupportedFlows()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SourceCard);
